import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../PermissionsProvider";
import { SessionService } from "../../../services/SessionService";
import { WorkflowService, Workflow, WorkflowRun } from "../../../services/WorkflowService";
import { Application } from "../../../model_gen/evaluation";
import { Session } from "../../../model_gen/session";
import { Card, CardContent } from "../../ui/card";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon, XIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import moment from "moment";
import WorkflowRunDetailModal, { formatWorkflowDuration } from "../../shared/WorkflowRunDetailModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import { Spinner } from "../../ui/spinner";
import { Badge } from "../../ui/badge";

export type WorkflowItemSource = "workflow_runs" | "sessions";

interface TestSetSessionsProps {
  application: Application;
  sourceType: WorkflowItemSource;
  selectedWorkflowRunIds: number[];
  selectedSessionIds: string[];
  onChange: (changes: {
    workflowItemSource?: WorkflowItemSource;
    workflowRunIds?: number[];
    sessionIds?: string[];
    sessionTransformWorkflowId?: number;
  }) => void;
}

const PAGE_SIZE = 10;
const MAX_ITEMS = 200;

const TestSetSessions: React.FC<TestSetSessionsProps> = ({
  application,
  sourceType,
  selectedWorkflowRunIds,
  selectedSessionIds,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const applicationId = application.id!;

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [selectedWorkflowFilters, setSelectedWorkflowFilters] = useState<number[]>([]);

  useEffect(() => {
    setWorkflowsLoading(true);
    WorkflowService.getWorkflows(() => {}, apiKey).subscribe(
      (data: Workflow[]) => {
        const appWorkflows = (data || []).filter(
          (w) => w.application_id === applicationId
        );
        setWorkflows(appWorkflows);
        setWorkflowsLoading(false);
      },
      () => {
        setWorkflowsLoading(false);
      }
    );
  }, [applicationId, apiKey]);

  const runCount = selectedWorkflowRunIds.length;
  const sessionCount = selectedSessionIds.length;
  const hasAnySelection = runCount > 0 || sessionCount > 0;

  const toggleWorkflowFilter = (wfId: number) => {
    setSelectedWorkflowFilters((prev) =>
      prev.includes(wfId)
        ? prev.filter((id) => id !== wfId)
        : [...prev, wfId]
    );
  };

  const clearWorkflowFilters = () => {
    setSelectedWorkflowFilters([]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground mb-2">
          Select Items
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose workflow runs or sessions from {application.application_name} to include in the test set. Maximum {MAX_ITEMS} items.
        </p>
      </div>

      {hasAnySelection && (
        <Card className="border-maitai-green bg-maitai-green/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 text-sm flex-wrap">
              {runCount > 0 && (
                <span className="font-gc-med text-foreground">
                  {runCount} run{runCount !== 1 ? "s" : ""} selected
                  <span className="text-muted-foreground font-normal ml-1">(added directly)</span>
                </span>
              )}
              {sessionCount > 0 && (
                <span className="font-gc-med text-foreground">
                  {sessionCount} session{sessionCount !== 1 ? "s" : ""} selected
                  <span className="text-muted-foreground font-normal ml-1">(converted to runs)</span>
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="border-b border-border">
        <nav className="flex gap-4">
          <button
            type="button"
            onClick={() => onChange({ workflowItemSource: "workflow_runs" })}
            className={cn(
              "pb-3 text-sm font-gc-med border-b-2 -mb-px transition-colors",
              sourceType === "workflow_runs"
                ? "border-maitai-green text-maitai-green"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Workflow Runs
          </button>
          <button
            type="button"
            onClick={() => onChange({ workflowItemSource: "sessions" })}
            className={cn(
              "pb-3 text-sm font-gc-med border-b-2 -mb-px transition-colors",
              sourceType === "sessions"
                ? "border-maitai-green text-maitai-green"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Sessions
          </button>
        </nav>
      </div>

      {sourceType === "workflow_runs" ? (
        <WorkflowRunsTab
          applicationId={applicationId}
          workflows={workflows}
          workflowsLoading={workflowsLoading}
          selectedWorkflowFilters={selectedWorkflowFilters}
          onToggleWorkflowFilter={toggleWorkflowFilter}
          onClearWorkflowFilters={clearWorkflowFilters}
          selectedIds={selectedWorkflowRunIds}
          onChange={(ids) => onChange({ workflowRunIds: ids })}
          apiKey={apiKey}
        />
      ) : (
        <SessionsTab
          applicationId={applicationId}
          workflows={workflows}
          selectedIds={selectedSessionIds}
          onChange={(ids, transformWorkflowId) =>
            onChange({ sessionIds: ids, sessionTransformWorkflowId: transformWorkflowId })
          }
          apiKey={apiKey}
        />
      )}
    </div>
  );
};

interface WorkflowRunsTabProps {
  applicationId: number;
  workflows: Workflow[];
  workflowsLoading: boolean;
  selectedWorkflowFilters: number[];
  onToggleWorkflowFilter: (wfId: number) => void;
  onClearWorkflowFilters: () => void;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  apiKey: any;
}

const WorkflowRunsTab: React.FC<WorkflowRunsTabProps> = ({
  applicationId,
  workflows,
  workflowsLoading,
  selectedWorkflowFilters,
  onToggleWorkflowFilter,
  onClearWorkflowFilters,
  selectedIds,
  onChange,
  apiKey,
}) => {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkflowRun, setSelectedWorkflowRun] = useState<WorkflowRun | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedWorkflowFilters.join(",")]);

  useEffect(() => {
    setLoading(true);
    const offset = (currentPage - 1) * PAGE_SIZE;
    const filterIds = selectedWorkflowFilters.length > 0 ? selectedWorkflowFilters : undefined;
    WorkflowService.getWorkflowRuns(
      applicationId,
      offset,
      PAGE_SIZE,
      () => {},
      apiKey,
      filterIds
    ).subscribe(
      (data: { runs?: WorkflowRun[]; total?: number } | WorkflowRun[]) => {
        const resp = data as { runs?: WorkflowRun[]; total?: number };
        const list = resp.runs ?? (Array.isArray(data) ? data : []);
        const total = resp.total ?? list.length;
        setRuns(list);
        setTotalRuns(total);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching workflow runs:", error);
        setLoading(false);
      }
    );
  }, [applicationId, currentPage, selectedWorkflowFilters.join(","), apiKey]);

  const totalPages = Math.max(1, Math.ceil(totalRuns / PAGE_SIZE));
  const hasFilters = selectedWorkflowFilters.length > 0;

  const handleToggle = (runId: number, add: boolean) => {
    if (add) {
      if (selectedIds.length >= MAX_ITEMS) return;
      onChange([...selectedIds, runId]);
    } else {
      onChange(selectedIds.filter((id) => id !== runId));
    }
  };

  const handleSelectPage = (add: boolean) => {
    const pageIds = runs.map((r) => r.id);
    if (add) {
      const newIds = pageIds.filter((id) => !selectedIds.includes(id));
      const remaining = MAX_ITEMS - selectedIds.length;
      onChange([...selectedIds, ...newIds.slice(0, remaining)]);
    } else {
      onChange(selectedIds.filter((id) => !pageIds.includes(id)));
    }
  };

  const allPageSelected =
    runs.length > 0 && runs.every((r) => selectedIds.includes(r.id));
  const somePageSelected = runs.some((r) => selectedIds.includes(r.id));

  const formatDate = (s: string | undefined) => {
    if (!s) return "-";
    return moment(s).format("MMM D, YYYY h:mm A");
  };

  const getWorkflowName = (run: WorkflowRun): string => {
    const runAny = run as any;
    if (runAny.workflow_name) return runAny.workflow_name;
    const wf = workflows.find((w) => w.id === run.workflow_id);
    return wf?.workflow_name ?? "-";
  };

  return (
    <div className="space-y-4">
      {/* Workflow filter chips */}
      {workflows.length > 1 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-gc-med text-foreground">
              Filter by Workflow
            </Label>
            {hasFilters && (
              <button
                type="button"
                onClick={onClearWorkflowFilters}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {workflowsLoading ? (
              <span className="text-sm text-muted-foreground">Loading workflows...</span>
            ) : (
              workflows.map((wf) => {
                const isActive = selectedWorkflowFilters.includes(wf.id);
                return (
                  <Badge
                    key={wf.id}
                    variant={isActive ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer select-none transition-colors",
                      isActive
                        ? "bg-maitai-green hover:bg-maitai-green/80 text-white border-maitai-green"
                        : "hover:bg-accent/50"
                    )}
                    onClick={() => onToggleWorkflowFilter(wf.id)}
                  >
                    {wf.workflow_name}
                    {isActive && <XIcon className="w-3 h-3 ml-1" />}
                  </Badge>
                );
              })
            )}
          </div>
          {!hasFilters && (
            <p className="text-xs text-muted-foreground">
              Showing all workflows. Click to filter.
            </p>
          )}
        </div>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    somePageSelected && !allPageSelected ? "indeterminate" : allPageSelected
                  }
                  onCheckedChange={(checked) => handleSelectPage(checked === true)}
                  disabled={loading || runs.length === 0}
                />
              </TableHead>
              <TableHead>Session ID</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Loading workflow runs...
                </TableCell>
              </TableRow>
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {hasFilters
                    ? "No workflow runs found for the selected filters."
                    : "No workflow runs found. Execute a workflow to create runs."}
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => {
                const isSelected = selectedIds.includes(run.id);
                return (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer hover:bg-accent/20 transition-colors"
                    onClick={() => {
                      setSelectedWorkflowRun(run);
                      setDetailModalVisible(true);
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleToggle(run.id, !!checked)}
                        disabled={!isSelected && selectedIds.length >= MAX_ITEMS}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {run.session_id?.length > 20
                        ? `...${run.session_id.slice(-16)}`
                        : run.session_id ?? "-"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getWorkflowName(run)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm font-mono">
                      {formatWorkflowDuration(run.profile)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(run.date_created)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <div className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} ({totalRuns} total)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      <WorkflowRunDetailModal
        run={selectedWorkflowRun}
        open={detailModalVisible}
        onOpenChange={(open) => {
          setDetailModalVisible(open);
          if (!open) setSelectedWorkflowRun(null);
        }}
      />
    </div>
  );
};

interface TransformPreviewResult {
  input: unknown;
  output: unknown;
}

interface SessionsTabProps {
  applicationId: number;
  workflows: Workflow[];
  selectedIds: string[];
  onChange: (ids: string[], transformWorkflowId?: number) => void;
  apiKey: any;
}

const SessionsTab: React.FC<SessionsTabProps> = ({
  applicationId,
  workflows,
  selectedIds,
  onChange,
  apiKey,
}) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<TransformPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);

  const transformWorkflow = workflows.find((w) => w.id > 0) ?? null;

  const handleTransformPreview = (sessionId: string) => {
    if (!transformWorkflow) return;
    setPreviewSessionId(sessionId);
    setPreviewResult(null);
    setPreviewError(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    WorkflowService.transformPreview(transformWorkflow.id, sessionId, () => {}, apiKey).subscribe(
      (data: { input?: unknown; output?: unknown; error?: { message?: string } }) => {
        if (data.error) {
          setPreviewError(data.error.message || "Transform failed");
        } else {
          setPreviewResult({ input: data.input, output: data.output });
        }
        setPreviewLoading(false);
      },
      (err: any) => {
        setPreviewError(err?.message || "Transform request failed");
        setPreviewLoading(false);
      }
    );
  };

  useEffect(() => {
    if (!applicationId) return;
    setLoading(true);
    const offset = (currentPage - 1) * PAGE_SIZE;
    SessionService.getApplicationSessions(
      applicationId,
      offset,
      PAGE_SIZE,
      () => {},
      apiKey
    ).subscribe(
      (data: { sessions?: Session[]; total?: number }) => {
        const list = data.sessions || [];
        const total = data.total ?? 0;
        setSessions(list);
        setTotalSessions(total);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching sessions:", error);
        setLoading(false);
      }
    );
  }, [applicationId, currentPage, apiKey]);

  const totalPages = Math.max(1, Math.ceil(totalSessions / PAGE_SIZE));

  const wfId = transformWorkflow?.id;

  const handleToggle = (sessionId: string, add: boolean) => {
    if (add) {
      if (selectedIds.length >= MAX_ITEMS) return;
      onChange([...selectedIds, sessionId], wfId);
    } else {
      const next = selectedIds.filter((id) => id !== sessionId);
      onChange(next, next.length > 0 ? wfId : undefined);
    }
  };

  const handleSelectPage = (add: boolean) => {
    const pageIds = sessions.map((s) => s.session_id);
    if (add) {
      const newIds = pageIds.filter((id) => !selectedIds.includes(id));
      const remaining = MAX_ITEMS - selectedIds.length;
      onChange([...selectedIds, ...newIds.slice(0, remaining)], wfId);
    } else {
      const next = selectedIds.filter((id) => !pageIds.includes(id));
      onChange(next, next.length > 0 ? wfId : undefined);
    }
  };

  const allPageSelected =
    sessions.length > 0 && sessions.every((s) => selectedIds.includes(s.session_id));
  const somePageSelected = sessions.some((s) => selectedIds.includes(s.session_id));

  const formatDate = (ts: number | undefined) => {
    if (!ts || ts <= 0) return "-";
    return moment(ts).format("MMM D, YYYY h:mm A");
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-gc-med text-foreground">
        Available Sessions
      </Label>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    somePageSelected && !allPageSelected ? "indeterminate" : allPageSelected
                  }
                  onCheckedChange={(checked) => handleSelectPage(checked === true)}
                  disabled={loading || sessions.length === 0}
                />
              </TableHead>
              <TableHead>Session ID</TableHead>
              <TableHead>Requests</TableHead>
              <TableHead>Date</TableHead>
              {transformWorkflow && (
                <TableHead className="w-24 text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={transformWorkflow ? 5 : 4} className="text-center py-8 text-muted-foreground">
                  Loading sessions...
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={transformWorkflow ? 5 : 4} className="text-center py-8 text-muted-foreground">
                  No sessions found for this application.
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => {
                const isSelected = selectedIds.includes(session.session_id);
                return (
                  <TableRow key={session.session_id}>
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleToggle(session.session_id, !!checked)
                        }
                        disabled={!isSelected && selectedIds.length >= MAX_ITEMS}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {session.session_id.length > 20
                        ? `...${session.session_id.slice(-16)}`
                        : session.session_id}
                    </TableCell>
                    <TableCell>{session.request_count ?? "-"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(session.date_ended ?? session.date_created)}
                    </TableCell>
                    {transformWorkflow && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 h-7 text-xs"
                          onClick={() => handleTransformPreview(session.session_id)}
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                          Preview
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <div className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} ({totalSessions} total)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transform Preview</DialogTitle>
            <DialogDescription>
              {previewSessionId && (
                <span className="font-mono text-xs">
                  Session: {previewSessionId.length > 32
                    ? `...${previewSessionId.slice(-28)}`
                    : previewSessionId}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Spinner size="md" />
              <p className="text-sm text-muted-foreground">Running transform_session...</p>
            </div>
          ) : previewError ? (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
              {previewError}
            </div>
          ) : previewResult ? (
            <div className="space-y-4">
              <div className="rounded-md border border-border overflow-hidden">
                <div className="bg-muted/50 border-b border-border px-3 py-2 text-xs font-gc-med text-muted-foreground uppercase tracking-wide">
                  Input
                </div>
                <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-48 overflow-y-auto bg-background">
                  {JSON.stringify(previewResult.input, null, 2)}
                </pre>
              </div>
              <div className="rounded-md border border-border overflow-hidden">
                <div className="bg-muted/50 border-b border-border px-3 py-2 text-xs font-gc-med text-muted-foreground uppercase tracking-wide">
                  Output
                </div>
                <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-48 overflow-y-auto bg-background">
                  {JSON.stringify(previewResult.output, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestSetSessions;
